import { useEffect } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { useGameStore } from "@/store/gameStore";
import { gameDocToGame } from "@/hooks/convex/gameMapping";

const isFromPreviousDay = (timestamp: number | undefined): boolean => {
  if (!timestamp) return false;

  const updatedDate = new Date(timestamp);
  const today = new Date();

  updatedDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return updatedDate < today;
};

export function useConvexGame() {
  const slug = useGameStore((state) => state.game.slug);
  const syncStatus = useGameStore((state) => state.game.syncStatus);
  const setGame = useGameStore((state) => state.setGame);
  const unShareGame = useGameStore((state) => state.unShareGame);
  const ensureRound = useMutation(api.game.ensureRound);

  const convexGame = useQuery(api.game.get, slug ? { slug } : "skip");

  useEffect(() => {
    const currentGame = useGameStore.getState().game;

    const shouldConvertToLocalOnly =
      (currentGame.slug && convexGame === null) ||
      (convexGame && isFromPreviousDay(convexGame.updatedAt));

    if (shouldConvertToLocalOnly) {
      unShareGame();
    } else if (convexGame && !convexGame.roundId) {
      let active = true;
      void ensureRound({ slug: convexGame.slug })
        .then((protectedGame) => {
          if (
            active &&
            protectedGame &&
            useGameStore.getState().game.slug === convexGame.slug
          ) {
            setGame(gameDocToGame(protectedGame));
          }
        })
        .catch((error: unknown) => {
          console.error("Round upgrade error:", error);
          if (active && useGameStore.getState().game.slug === convexGame.slug) {
            useGameStore.getState().setSyncStatus("failed");
          }
        });
      return () => {
        active = false;
      };
    } else {
      if (
        convexGame &&
        JSON.stringify(currentGame) !==
          JSON.stringify(gameDocToGame(convexGame))
      ) {
        setGame(gameDocToGame(convexGame));
      }
    }
  }, [convexGame, ensureRound, slug, setGame, unShareGame]);

  return {
    convexGame: slug ? convexGame : null,
    isSynced: !!slug,
    syncStatus,
  };
}
